import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * El cuerpo de una ficha, por bloques y al estilo de Notion.
 *
 * Un post no es un campo de texto largo: es una sucesión de cosas distintas
 * —párrafos, títulos, una imagen a media anchura, un vídeo, una llamada a la
 * acción—. Con una barra de herramientas fija arriba hay que saber de antemano
 * qué se puede meter y dónde; aquí se pregunta donde está el cursor, que es
 * donde se está mirando: el «+» del margen y la barra que aparece al escribir
 * «/» abren la misma lista.
 *
 * Se guarda HTML, el mismo que ya entendían la página publicada y el lienzo: un
 * modelo de bloques propio obligaría a migrar lo escrito y a mantener dos
 * dibujantes. Los bloques que no existen en HTML llano —el destacado, el botón,
 * la imagen con tamaño— son marcado corriente con una clase, y su aspecto vive
 * en `richContentCss`, así que se ven igual aquí, en el lienzo y publicados.
 *
 * Las acciones del bloque y el reordenamiento se comparten entre blog y CMS.
 */
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconAlignCenter, IconAlignJustified, IconAlignLeft, IconAlignRight, IconArrowDown, IconArrowUp, IconBlockquote, IconBold, IconBrandYoutube, IconCheck, IconChevronDown, IconCode, IconCopy, IconGripVertical, IconH1, IconLetterCase, IconH2, IconH3, IconInfoCircle, IconItalic, IconLetterT, IconLink, IconList, IconListNumbers, IconMinus, IconPhoto, IconPencil, IconPlus, IconPointer, IconStrikethrough, IconTrash, IconUnderline, } from '@tabler/icons-react';
const BlockEditorEnvContext = createContext(null);
export function BlockEditorEnvProvider({ value, children }) {
    return _jsx(BlockEditorEnvContext.Provider, { value: value, children: children });
}
function useEnv() {
    const env = useContext(BlockEditorEnvContext);
    if (!env)
        throw new Error('BlockEditor necesita un BlockEditorEnvProvider alrededor.');
    return env;
}
function useI18n() {
    return { t: useEnv().t };
}
// ── Utilidades de texto ──────────────────────────────────────────────────────
function escapeText(value) {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escapeAttr(value) {
    return escapeText(value).replace(/"/g, '&quot;');
}
/** El id de un vídeo de YouTube, venga como venga el enlace. */
function youtubeId(url) {
    const trimmed = url.trim();
    const match = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([\w-]{11})/.exec(trimmed);
    if (match?.[1])
        return match[1];
    return /^[\w-]{11}$/.test(trimmed) ? trimmed : null;
}
/** Y el de Vimeo, que es el otro que se pega a diario. */
function vimeoId(url) {
    const match = /vimeo\.com\/(?:video\/)?(\d{6,})/.exec(url.trim());
    return match?.[1] ?? null;
}
/** El reproductor de un enlace de vídeo, o nada si no lo es. */
export function embedHtml(raw) {
    const youtube = youtubeId(raw);
    if (youtube)
        return `<div class="cms-embed"><iframe src="https://www.youtube.com/embed/${youtube}" title="YouTube" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>`;
    const vimeo = vimeoId(raw);
    if (vimeo)
        return `<div class="cms-embed"><iframe src="https://player.vimeo.com/video/${vimeo}" title="Vimeo" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe></div>`;
    return null;
}
/** Texto pegado → párrafos, respetando los renglones en blanco. */
export function pastedToHtml(value) {
    const normalized = value.replace(/\r\n?/g, '\n').trim();
    if (!normalized)
        return '<p><br></p>';
    return normalized
        .split(/\n{2,}/)
        .map((block) => `<p>${block.split('\n').map((line) => escapeText(line.trim())).filter(Boolean).join('<br>')}</p>`)
        .join('');
}
/* El grupo es un id; lo que se lee, la traducción. Se mostraba el id, así que
   la lista decía «Texto» también en inglés. */
const GROUP_LABEL_KEY = { Texto: 'blk.groupText', Medios: 'blk.groupMedia', Venta: 'blk.groupSale' };
const BLOCKS = [
    { id: 'text', group: 'Texto', labelKey: 'blk.text', hintKey: 'blk.paragraphHint', keywordsKey: 'blkKw.text', icon: _jsx(IconLetterT, { size: 16 }), action: { kind: 'format', tag: 'p' }, turnInto: { kind: 'tag', tag: 'p' } },
    { id: 'h1', group: 'Texto', labelKey: 'blk.title1', hintKey: 'blk.h1Hint', keywordsKey: 'blkKw.h1', icon: _jsx(IconH1, { size: 16 }), action: { kind: 'format', tag: 'h1' }, turnInto: { kind: 'tag', tag: 'h1' }, shortcut: '#' },
    { id: 'h2', group: 'Texto', labelKey: 'blk.title2', hintKey: 'blk.h2Hint', keywordsKey: 'blkKw.h2', icon: _jsx(IconH2, { size: 16 }), action: { kind: 'format', tag: 'h2' }, turnInto: { kind: 'tag', tag: 'h2' }, shortcut: '##' },
    { id: 'h3', group: 'Texto', labelKey: 'blk.title3', hintKey: 'blk.h3Hint', keywordsKey: 'blkKw.h3', icon: _jsx(IconH3, { size: 16 }), action: { kind: 'format', tag: 'h3' }, turnInto: { kind: 'tag', tag: 'h3' }, shortcut: '###' },
    { id: 'bullet', group: 'Texto', labelKey: 'blk.list', hintKey: 'blk.listHint', keywordsKey: 'blkKw.bullet', icon: _jsx(IconList, { size: 16 }), action: { kind: 'list', command: 'insertUnorderedList' }, turnInto: { kind: 'list', tag: 'ul' }, shortcut: '-' },
    { id: 'numbered', group: 'Texto', labelKey: 'blk.numbered', hintKey: 'blk.numberedHint', keywordsKey: 'blkKw.numbered', icon: _jsx(IconListNumbers, { size: 16 }), action: { kind: 'list', command: 'insertOrderedList' }, turnInto: { kind: 'list', tag: 'ol' }, shortcut: '1.' },
    { id: 'quote', group: 'Texto', labelKey: 'blk.quote', hintKey: 'blk.quoteHint', keywordsKey: 'blkKw.quote', icon: _jsx(IconBlockquote, { size: 16 }), action: { kind: 'format', tag: 'blockquote' }, turnInto: { kind: 'tag', tag: 'blockquote' }, shortcut: '>' },
    { id: 'callout', group: 'Texto', labelKey: 'blk.callout', hintKey: 'blk.calloutHint', keywordsKey: 'blkKw.callout', icon: _jsx(IconInfoCircle, { size: 16 }), action: { kind: 'html', caretInside: true, html: calloutHtml }, turnInto: { kind: 'callout' } },
    { id: 'code', group: 'Texto', labelKey: 'blk.code', hintKey: 'blk.codeHint', keywordsKey: 'blkKw.code', icon: _jsx(IconCode, { size: 16 }), action: { kind: 'html', caretInside: true, html: codeHtml }, turnInto: { kind: 'code' }, shortcut: '```' },
    { id: 'divider', group: 'Texto', labelKey: 'blk.divider', hintKey: 'blk.dividerHint', keywordsKey: 'blkKw.divider', icon: _jsx(IconMinus, { size: 16 }), action: { kind: 'html', html: '<hr>' }, shortcut: '---' },
    { id: 'image', group: 'Medios', labelKey: 'blk.image', hintKey: 'blk.imageHint', keywordsKey: 'blkKw.image', icon: _jsx(IconPhoto, { size: 16 }), action: { kind: 'media', media: 'image' } },
    { id: 'video', group: 'Medios', labelKey: 'blk.video', hintKey: 'blk.videoHint2', keywordsKey: 'blkKw.video', icon: _jsx(IconBrandYoutube, { size: 16 }), action: { kind: 'media', media: 'video' } },
    { id: 'cta', group: 'Venta', labelKey: 'blk.cta', hintKey: 'blk.ctaHint', keywordsKey: 'blkKw.cta', icon: _jsx(IconPointer, { size: 16 }), action: { kind: 'html', caretInside: true, html: ctaHtml }, turnInto: { kind: 'cta' } },
];
/* El marcado que se inserta lleva texto dentro, así que se arma al usarlo y no
   al cargar el módulo: si no, el texto de ejemplo se quedaría en el idioma que
   hubiera al arrancar. */
function calloutHtml(translate) {
    return `<div class="cms-callout"><span class="cms-callout-mark">💡</span><div class="cms-callout-body"><p>${translate('blk.calloutPlaceholder')}</p></div></div>`;
}
function codeHtml(translate) {
    return `<pre><code>${translate('blk.codeSample')}</code></pre>`;
}
function ctaHtml(translate) {
    return `<p class="cms-cta"><a href="#">${translate('blk.buyNow')}</a></p>`;
}
/** Los anchos de una imagen, en el mismo lenguaje que se usa al hablar. */
const IMAGE_SIZES = [
    { id: 'small', labelKey: 'blk.widthS' },
    { id: 'medium', labelKey: 'blk.widthM' },
    { id: 'large', labelKey: 'blk.widthL' },
    { id: 'full', labelKey: 'blk.widthFull' },
];
/**
 * El texto de un bloque, con sus negritas y sus enlaces pero sin su envoltura.
 *
 * De una cita se saca lo que hay dentro, de un elemento de lista lo suyo, de un
 * destacado su cuerpo y de un bloque de código su texto llano. Es lo que se
 * mueve de una forma a otra al convertir.
 */
export function blockInnerHtml(el) {
    if (el.tagName === 'PRE')
        return escapeText(el.textContent ?? '');
    const callout = el.classList.contains('cms-callout') ? el.querySelector('.cms-callout-body') : null;
    if (callout)
        return callout.innerHTML;
    if (el.tagName === 'UL' || el.tagName === 'OL') {
        return Array.from(el.children).map((item) => item.innerHTML).join('<br>');
    }
    return el.innerHTML;
}
/** Qué forma tiene ya un bloque, para señalarla al ofrecer las demás. */
export function blockKindOf(el) {
    if (!el)
        return null;
    if (el.classList.contains('cms-callout'))
        return 'callout';
    if (el.classList.contains('cms-cta'))
        return 'cta';
    switch (el.tagName) {
        case 'H1': return 'h1';
        case 'H2': return 'h2';
        case 'H3': return 'h3';
        case 'UL': return 'bullet';
        case 'OL': return 'numbered';
        case 'BLOCKQUOTE': return 'quote';
        case 'PRE': return 'code';
        case 'FIGURE': return 'image';
        case 'HR': return 'divider';
        case 'P': return 'text';
        default: return null;
    }
}
/** La envoltura que pide cada forma, ya con el texto dentro. */
export function buildTurned(target, inner) {
    const html = inner.trim() ? inner : '<br>';
    const holder = document.createElement('div');
    if (target.kind === 'tag')
        holder.innerHTML = `<${target.tag}>${html}</${target.tag}>`;
    else if (target.kind === 'list')
        holder.innerHTML = `<${target.tag}>${html.split(/<br\s*\/?>/i).map((line) => `<li>${line || '<br>'}</li>`).join('')}</${target.tag}>`;
    else if (target.kind === 'callout')
        holder.innerHTML = `<div class="cms-callout"><span class="cms-callout-mark">💡</span><div class="cms-callout-body"><p>${html}</p></div></div>`;
    else if (target.kind === 'code')
        holder.innerHTML = `<pre><code>${html.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '')}</code></pre>`;
    else
        holder.innerHTML = `<p class="cms-cta"><a href="#">${html.replace(/<[^>]*>/g, '') || 'Comprar ahora'}</a></p>`;
    return holder.firstElementChild;
}
/**
 * Los atajos de Markdown al principio de un renglón, como en Notion: «# » hace
 * un título, «- » una lista, «> » una cita… `prefix` es lo escrito antes del
 * cursor. El espacio puede llegar como espacio duro: el navegador lo pone así
 * al final de un renglón para que no se pierda.
 */
export function markdownShortcut(prefix) {
    const text = prefix.replace(/\u00a0/g, ' ');
    if (text === '```')
        return { kind: 'code' };
    if (text === '---')
        return 'divider';
    switch (text) {
        case '# ': return { kind: 'tag', tag: 'h1' };
        case '## ': return { kind: 'tag', tag: 'h2' };
        case '### ': return { kind: 'tag', tag: 'h3' };
        case '- ':
        case '* ': return { kind: 'list', tag: 'ul' };
        case '1. ': return { kind: 'list', tag: 'ol' };
        case '> ': return { kind: 'tag', tag: 'blockquote' };
        default: return null;
    }
}
/** Cuántos caracteres de texto hay entre el principio del bloque y el cursor; `null` sin cursor dentro. */
function caretOffsetIn(block) {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !selection.isCollapsed)
        return null;
    const range = selection.getRangeAt(0);
    if (!block.contains(range.startContainer))
        return null;
    const before = document.createRange();
    before.selectNodeContents(block);
    before.setEnd(range.startContainer, range.startOffset);
    return before.toString().length;
}
/** Quita los primeros `count` caracteres del texto de un nodo, sin tocar sus negritas ni enlaces. */
function dropLeadingText(node, count) {
    let left = count;
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    while (left > 0) {
        const text = walker.nextNode();
        if (!text)
            break;
        const take = Math.min(left, text.data.length);
        text.data = text.data.slice(take);
        left -= take;
    }
}
const TEXT_ALIGNS = ['left', 'center', 'right', 'justify'];
const ALIGN_COMMAND = { left: 'justifyLeft', center: 'justifyCenter', right: 'justifyRight', justify: 'justifyFull' };
const ALIGN_ICON = {
    left: _jsx(IconAlignLeft, { size: 14 }),
    center: _jsx(IconAlignCenter, { size: 14 }),
    right: _jsx(IconAlignRight, { size: 14 }),
    justify: _jsx(IconAlignJustified, { size: 14 }),
};
/** Los bloques que, vacíos o con el cursor al principio, vuelven a ser un párrafo como en Notion. */
const UNWRAPPABLE = new Set(['h1', 'h2', 'h3', 'quote', 'callout', 'code', 'cta']);
/**
 * La tipografía del editor, que no es la del sitio.
 *
 * Aquí se escribe, no se maqueta. Con las medidas del tema, un «Título 1» de 64
 * píxeles ocupaba tres renglones de la ficha y empujaba el resto fuera de la
 * pantalla: el editor pasaba a ser una vista previa mala en vez de un sitio
 * cómodo donde teclear. Y encima engaña, porque en la página ese título hereda
 * el ancho, el color y el fondo de donde se coloque, que aquí no existen.
 *
 * Lo que se guarda no cambia ni un carácter: sigue siendo `<h1>`, y en el lienzo
 * y en la página publicada se ve con las medidas de verdad. Aquí sólo se decide
 * cómo de grande se ve mientras se escribe: una escala —32, 26, 21…— que
 * separa los niveles con claridad sin dominar el editor.
 *
 * Y los aires, más cortos que los de publicación por lo mismo: un párrafo cada
 * dos centímetros se lee como un formulario a medio rellenar, no como un texto.
 */
/** Un cuerpo vacío en el editor: un párrafo, para que haya dónde escribir. */
const EMPTY_BODY = '<p><br></p>';
const EDITOR_TYPOGRAPHY_CSS = [
    // La letra del editor, una sola para todo: la del sitio la pone el lienzo.
    '.cms-blocks-editor,.cms-blocks-editor *{font-family:"DM Sans",ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif!important;}',
    '.cms-blocks-editor pre,.cms-blocks-editor code{font-family:ui-monospace,SFMono-Regular,Menlo,monospace!important;}',
    // Sobre hoja blanca, y con el tema oscuro también: si no, texto blanco sobre
    // blanco en cuanto la marca es oscura.
    '.cms-blocks-editor,.cms-blocks-editor p,.cms-blocks-editor li,.cms-blocks-editor h1,.cms-blocks-editor h2,.cms-blocks-editor h3,.cms-blocks-editor h4,.cms-blocks-editor h5,.cms-blocks-editor h6,.cms-blocks-editor blockquote{color:#1a1a1a;}',
    '.cms-blocks-editor a{color:#3b82f6;}', // accent-ok: CSS del contenido del cliente
    // Una escala más marcada para distinguir los niveles de título.
    '.cms-blocks-editor p,.cms-blocks-editor li{font-size:15px;line-height:1.7;font-weight:400;}',
    '.cms-blocks-editor h1{font-size:32px;line-height:1.2;font-weight:600;letter-spacing:-0.02em;}',
    '.cms-blocks-editor h2{font-size:26px;line-height:1.25;font-weight:600;letter-spacing:-0.015em;}',
    '.cms-blocks-editor h3{font-size:21px;line-height:1.3;font-weight:600;letter-spacing:-0.01em;}',
    '.cms-blocks-editor h4{font-size:14px;line-height:1.4;font-weight:600;}',
    '.cms-blocks-editor h5{font-size:13px;line-height:1.4;font-weight:600;}',
    '.cms-blocks-editor h6{font-size:12.5px;line-height:1.4;font-weight:600;color:#4b5563;text-transform:uppercase;letter-spacing:0.04em;}',
    // Los aires: apretados entre iguales, con un respiro antes de un título.
    '.cms-blocks-editor p{margin:0 0 2px;}',
    '.cms-blocks-editor h1,.cms-blocks-editor h2,.cms-blocks-editor h3,.cms-blocks-editor h4,.cms-blocks-editor h5,.cms-blocks-editor h6{margin:14px 0 2px;}',
    '.cms-blocks-editor > :first-child{margin-top:0;}',
    '.cms-blocks-editor ul,.cms-blocks-editor ol{margin:2px 0 2px 1.15em;}',
    '.cms-blocks-editor li{margin:1px 0;}',
    '.cms-blocks-editor blockquote{margin:8px 0;padding-left:14px;font-size:15px;}',
    '.cms-blocks-editor hr{display:block;width:100%;max-width:none;height:1px;margin:22px 0;padding:0;border:0;background:#d1d5db;opacity:1;}',
    '.cms-blocks-editor pre{margin:8px 0;padding:10px 12px;font-size:12.5px;}',
    '.cms-blocks-editor figure.cms-figure{margin:10px 0;}',
    '.cms-blocks-editor figure.cms-figure[data-align="center"]{margin-left:auto;margin-right:auto;}',
    '.cms-blocks-editor .cms-embed{margin:10px 0;}',
    '.cms-blocks-editor .cms-callout{margin:10px 0;padding:10px 12px;}',
    '.cms-blocks-editor .cms-cta{margin:10px 0;}',
    // Un botón neutro mientras se escribe; el tema real se aplica al publicar.
    '.cms-blocks-editor .cms-cta a,.cms-blocks-editor .cms-cta a:hover,.cms-blocks-editor .cms-cta a:active,.cms-blocks-editor .cms-cta a:focus-visible{all:unset;display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;padding:10px 18px;border:1px solid #2563eb;border-radius:9px;background:#2563eb;color:#fff;font-size:14px;font-weight:600;line-height:1.4;cursor:text;}',
    '.cms-blocks-editor .cms-cta a:hover{background:#1d4ed8;border-color:#1d4ed8;}',
    '.cms-blocks-editor .cms-cta a:focus-visible{outline:2px solid #9ca3af;outline-offset:2px;}',
].join('');
// ── El editor ────────────────────────────────────────────────────────────────
/** Un bloque sin texto ni imagen, vídeo ni separador. */
const isBlankBlock = (node) => !node.textContent?.trim()
    && !node.matches('hr, img, iframe, video, figure')
    && !node.querySelector('img, iframe, video, hr');
export function BlockEditor({ value, onCommit, onDraft, idleAdd = false }) {
    const { t, contentCss } = useEnv();
    const rootRef = useRef(null);
    const editorRef = useRef(null);
    const focused = useRef(false);
    /** El bloque donde está el cursor: de él cuelga el «+» y en él se inserta. */
    const blockRef = useRef(null);
    const savedRange = useRef(null);
    const [menu, setMenu] = useState(null);
    const [gutter, setGutter] = useState(null);
    /** Un arrastre acaba de soltarse: el clic que llega detrás no abre el menú. */
    const draggedRef = useRef(false);
    const dragActive = useRef(false);
    const dragCleanup = useRef(null);
    const [hoveredBlock, setHoveredBlock] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    useEffect(() => () => { dragCleanup.current?.(); }, []);
    /* Vacío por lo que se VE —ni texto ni imágenes—, no por el HTML: un cuerpo
       guardado puede traer párrafos vacíos de varias formas y, según cuál, la
       pista de «escribe algo» salía o no, dejando la hoja en blanco. */
    const [empty, setEmpty] = useState(!value);
    /** Dónde cuelga el «+» sin el cursor dentro (`idleAdd`): a la altura del último bloque. */
    const [idleTop, setIdleTop] = useState(null);
    /** Si el bloque donde está el cursor está vacío (mismo motivo). */
    const [blockBlank, setBlockBlank] = useState(true);
    const measure = useCallback(() => {
        const el = editorRef.current;
        if (!el)
            return;
        /* El navegador puede envolver una lista dentro de un DIV. Detectamos los
           bloques semánticos en cualquier nivel para ocultar la pista global. */
        const chosenBlock = el.querySelector('ul, ol, blockquote, h1, h2, h3, h4, h5, h6, pre, img, iframe, video, hr, figure');
        setEmpty(!el.textContent?.trim() && !chosenBlock);
        const last = el.lastElementChild;
        setIdleTop(last ? last.offsetTop : 0);
    }, []);
    const [media, setMedia] = useState(null);
    const [linkOpen, setLinkOpen] = useState(false);
    const linkBlockRef = useRef(null);
    const [figure, setFigure] = useState(null);
    /** La misma imagen seleccionada, legible sin depender del dibujado (ver `currentBlock`). */
    const figureRef = useRef(null);
    const pickFigure = useCallback((next) => {
        figureRef.current = next;
        setFigure(next);
    }, []);
    const [figureBox, setFigureBox] = useState(null);
    /** El ancho que se está arrastrando ahora mismo, para el rótulo. */
    const [figureWidth, setFigureWidth] = useState(null);
    // El valor entra sólo cuando no se está escribiendo: este editor no está
    // controlado —el navegador manda dentro de un contentEditable— y reescribirlo
    // mientras alguien teclea le mueve el cursor al principio.
    useEffect(() => {
        const el = editorRef.current;
        if (!el || focused.current)
            return;
        /* Vacío también lleva su párrafo: comparando con `value` a secas, un cuerpo
           vacío dejaba el editor sin ningún bloque —'' === ''— y lo que se tecleaba
           caía suelto, fuera de todo bloque, donde «/» no encontraba dónde abrir la
           lista (el caso de una entrada del blog nueva). */
        // Los campos antiguos de texto largo pueden traer texto plano con saltos.
        const html = value ? (/<[a-z][^>]*>/i.test(value) ? value : pastedToHtml(value)) : EMPTY_BODY;
        if (el.innerHTML !== html)
            el.innerHTML = html;
        el.querySelectorAll('hr, figure.cms-figure').forEach((block) => { block.setAttribute('contenteditable', 'false'); });
        measure();
    }, [value, measure]);
    const commit = useCallback(() => {
        const el = editorRef.current;
        if (!el)
            return;
        /* La pista «escribe algo» es del editor, no del post: si se quedó puesta en
           un bloque vacío, no se guarda. */
        const html = el.innerHTML
            .replace(/ data-block-hint="[^"]*"/g, '')
            .replace(/(<(?:hr|figure)\b[^>]*?)\scontenteditable="false"/gi, '$1');
        /* Y al revés: ese párrafo de arranque no es contenido. Entregarlo daría por
           cambiado un cuerpo que sigue vacío. */
        onCommit(html === EMPTY_BODY ? '' : html);
    }, [onCommit]);
    /**
     * Cuánto de la pantalla se ha llevado el teclado.
     *
     * `window.innerHeight` no se entera de que hay un teclado encima: sigue
     * midiendo la ventana entera, así que una barra pegada a `bottom: 0` se queda
     * debajo de las teclas. La ventana visual —lo que de verdad se ve— sí, y la
     * diferencia entre las dos es justo lo que hay que subir la barra.
     */
    const [keyboard, setKeyboard] = useState(0);
    useEffect(() => {
        const viewport = window.visualViewport;
        if (!viewport)
            return;
        const update = () => setKeyboard(Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop));
        update();
        viewport.addEventListener('resize', update);
        viewport.addEventListener('scroll', update);
        return () => {
            viewport.removeEventListener('resize', update);
            viewport.removeEventListener('scroll', update);
        };
    }, []);
    /** El hijo directo del editor donde vive el cursor. */
    const currentBlock = useCallback(() => {
        const el = editorRef.current;
        const selection = window.getSelection();
        if (!el || !selection || selection.rangeCount === 0)
            return null;
        let node = selection.anchorNode;
        if (!node || !el.contains(node))
            return null;
        /* Seleccionada una imagen —o cualquier bloque sin texto donde poner el
           cursor— el navegador ancla la selección en el propio editor, con el
           desplazamiento del hijo: no hay ningún nodo «dentro» que subir. Sin esto
           no había bloque actual y el asa y el lápiz desaparecían. */
        if (node === el) {
            const picked = figureRef.current;
            if (picked && el.contains(picked))
                return picked;
            const children = Array.from(el.children);
            return children[Math.min(selection.anchorOffset, children.length - 1)] ?? null;
        }
        while (node && node.parentNode !== el)
            node = node.parentNode;
        return node ?? null;
    }, []);
    /** Coloca el «+» a la altura del bloque del cursor. */
    const [currentKind, setCurrentKind] = useState(null);
    /* Si el bloque del cursor tiene vecino arriba y abajo. Va en estado —y no se
       lee del DOM al dibujar— porque de él dependen dos botones que se apagan: sin
       esto, «subir» seguiría encendido en el primer bloque y no haría nada, que es
       la peor respuesta posible a un toque. */
    const [blockEnds, setBlockEnds] = useState({ first: true, last: true });
    /* Con el foco puesto se enseña la barra de abajo, que es el «+» del margen
       traído a donde llega el pulgar. `focused` es una referencia y no dispara
       dibujado; esto sí, porque la barra aparece y desaparece con él. */
    const [writing, setWriting] = useState(false);
    const syncGutter = useCallback(() => {
        const el = editorRef.current;
        /* Con lo que hay ahora: «¿el cuerpo está vacío?» decide si se pinta encima la
           pista del cuerpo vacío, y sólo se recalculaba al escribir o al salir. Con
           el cuerpo vacío, elegir «Lista» desde el menú dejaba esa pista tapando la
           viñeta hasta la primera tecla. Esto pasa tras cada cambio de bloque y de
           selección, así que siempre va con lo último. */
        measure();
        const block = currentBlock();
        blockRef.current = block;
        setCurrentKind(blockKindOf(block));
        if (!el || !block) {
            setGutter(null);
            setBlockEnds({ first: true, last: true });
            return;
        }
        setGutter(block.offsetTop);
        setBlockBlank(isBlankBlock(block));
        setBlockEnds({ first: !block.previousElementSibling, last: !block.nextElementSibling });
        /* La pista de «escribe algo» sólo en el renglón donde está el cursor, como
           en Notion: puesta en todos los vacíos, un post a medias se llena de
           carteles grises. */
        /* Las listas ya muestran su viñeta o número. El navegador puede envolverlas
           en un DIV: tampoco ese contenedor debe llevar una pista encima. */
        const isList = block.matches('ul, ol') || !!block.querySelector('ul, ol');
        const hintOn = block;
        for (const marked of Array.from(el.querySelectorAll('[data-block-hint]'))) {
            if (marked !== hintOn)
                marked.removeAttribute('data-block-hint');
        }
        if (!isList && !block.textContent?.trim() && !block.querySelector('img, iframe, hr')) {
            /* Un título vacío dice qué título es, como en Notion: «escribe algo» en
               un renglón que ya es un Título 1 no ayuda a saber dónde se está. */
            const kind = blockKindOf(block);
            const hintKey = kind === 'h1' ? 'blk.title1' : kind === 'h2' ? 'blk.title2' : kind === 'h3' ? 'blk.title3' : kind === 'quote' ? 'blk.quote' : 'blk.placeholderLong';
            hintOn.setAttribute('data-block-hint', t(hintKey));
        }
        else {
            hintOn.removeAttribute('data-block-hint');
        }
    }, [currentBlock, measure]);
    const rememberRange = useCallback(() => {
        const selection = window.getSelection();
        const el = editorRef.current;
        savedRange.current = selection && selection.rangeCount > 0 && el && el.contains(selection.anchorNode)
            ? selection.getRangeAt(0).cloneRange()
            : null;
    }, []);
    const restoreRange = useCallback(() => {
        const el = editorRef.current;
        if (!el)
            return null;
        el.focus();
        const selection = window.getSelection();
        if (!selection)
            return null;
        if (savedRange.current) {
            selection.removeAllRanges();
            selection.addRange(savedRange.current);
        }
        else if (selection.rangeCount === 0) {
            const range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            selection.addRange(range);
        }
        return selection;
    }, []);
    /** Deja el cursor al final de un nodo. */
    function caretAtEnd(node) {
        const selection = window.getSelection();
        if (!selection)
            return;
        const range = document.createRange();
        if (node instanceof HTMLElement && node.matches('figure.cms-figure')) {
            // Una imagen es un bloque seleccionado, nunca un lugar para teclear.
            pickFigure(node);
            range.selectNode(node);
        }
        else {
            pickFigure(null);
            range.selectNodeContents(node);
            range.collapse(false);
        }
        selection.removeAllRanges();
        selection.addRange(range);
    }
    const selectedImage = useCallback(() => {
        const el = editorRef.current;
        const selection = window.getSelection();
        if (!el || !selection?.anchorNode || !selection.rangeCount || !el.contains(selection.anchorNode))
            return null;
        const anchor = selection.anchorNode instanceof Element ? selection.anchorNode : selection.anchorNode.parentElement;
        const image = anchor?.closest('figure.cms-figure');
        if (image && el.contains(image))
            return image;
        const range = selection.getRangeAt(0);
        if (range.startContainer === range.endContainer && range.endOffset - range.startOffset <= 1) {
            const block = range.startContainer.childNodes[range.startOffset];
            if (block instanceof HTMLElement && block.matches('figure.cms-figure'))
                return block;
        }
        return null;
    }, []);
    const writeOutsideImage = useCallback((image, before = false) => {
        const el = editorRef.current;
        if (!el)
            return;
        const neighbor = before ? image.previousElementSibling : image.nextElementSibling;
        let paragraph = neighbor?.tagName === 'P' && isBlankBlock(neighbor) ? neighbor : null;
        if (!paragraph) {
            paragraph = document.createElement('p');
            paragraph.innerHTML = '<br>';
            if (before)
                image.before(paragraph);
            else
                image.after(paragraph);
            onDraft?.(true);
        }
        blockRef.current = paragraph;
        el.focus({ preventScroll: true });
        caretAtEnd(paragraph);
        rememberRange();
        syncGutter();
    }, [onDraft, rememberRange, syncGutter]);
    useEffect(() => {
        const el = editorRef.current;
        if (!el)
            return;
        // beforeinput cubre también el teclado móvil, dictado y reemplazos de texto.
        const beforeInput = (event) => {
            if (!event.inputType.startsWith('insert'))
                return;
            const image = selectedImage();
            if (!image)
                return;
            event.preventDefault();
            writeOutsideImage(image);
            if (event.data)
                document.execCommand('insertText', false, event.data);
        };
        el.addEventListener('beforeinput', beforeInput);
        return () => el.removeEventListener('beforeinput', beforeInput);
    }, [selectedImage, writeOutsideImage]);
    /**
     * Mete un bloque suelto —una imagen, un separador, un vídeo— después del
     * bloque actual, o en su lugar si está vacío.
     *
     * Se hace con nodos y no con `insertHTML` porque este último mete lo que le
     * das DENTRO del párrafo donde esté el cursor: una imagen acababa colgando de
     * un `<p>` y un `<hr>` partía el párrafo en dos.
     */
    const insertBlockHtml = useCallback((html, caretInside = false) => {
        const el = editorRef.current;
        if (!el)
            return;
        el.focus();
        const block = blockRef.current && el.contains(blockRef.current) ? blockRef.current : currentBlock();
        const holder = document.createElement('div');
        holder.innerHTML = html;
        const nodes = Array.from(holder.childNodes);
        if (nodes.length === 0)
            return;
        holder.querySelectorAll('hr, figure.cms-figure').forEach((block) => { block.setAttribute('contenteditable', 'false'); });
        const empty = block ? !block.textContent?.trim() && !block.querySelector('img, iframe, hr') : false;
        if (block && empty) {
            for (const node of nodes)
                el.insertBefore(node, block);
            block.remove();
        }
        else if (block) {
            let anchor = block;
            for (const node of nodes) {
                el.insertBefore(node, anchor.nextSibling);
                anchor = node;
            }
        }
        else {
            for (const node of nodes)
                el.appendChild(node);
        }
        // Y siempre un párrafo detrás donde seguir escribiendo: sin él, un vídeo al
        // final del post deja el cursor sin sitio y no hay forma de continuar.
        const last = nodes[nodes.length - 1];
        let after = last.nextElementSibling;
        if (!after || after.tagName !== 'P' || after.textContent?.trim()) {
            after = document.createElement('p');
            after.innerHTML = '<br>';
            el.insertBefore(after, last.nextSibling);
        }
        const target = caretInside ? (last.querySelector('p, code, a') ?? last) : after;
        caretAtEnd(target);
        syncGutter();
        onDraft?.(true);
        commit();
    }, [commit, currentBlock, onDraft, syncGutter]);
    /**
     * Convierte el bloque actual en otra cosa, de verdad.
     *
     * Antes esto lo hacía `formatBlock`, y con una cita seleccionada elegir
     * «Título 1» dejaba un `<h1>` DENTRO de la cita: seguía siendo una cita, con
     * un titular dentro. Aquí se saca el texto de su envoltura, se construye la
     * nueva y se sustituye el nodo entero, que es lo que se quería decir.
     */
    const turnInto = useCallback((target) => {
        const el = editorRef.current;
        const block = blockRef.current && el?.contains(blockRef.current) ? blockRef.current : currentBlock();
        if (!el || !block)
            return;
        const replaced = buildTurned(target, blockInnerHtml(block));
        el.replaceChild(replaced, block);
        blockRef.current = replaced;
        el.focus();
        const inner = replaced.querySelector('li, p, code, a');
        caretAtEnd(inner ?? replaced);
        rememberRange();
        syncGutter();
        onDraft?.(true);
        commit();
    }, [commit, currentBlock, onDraft, rememberRange, syncGutter]);
    /**
     * Sube o baja un puesto el bloque donde está el cursor.
     *
     * Es la forma de reordenar en un teléfono. Arrastrar sería lo natural en
     * escritorio, pero dentro de un `contentEditable` el dedo ya significa otras
     * dos cosas —colocar el cursor y seleccionar texto—, y un tercer gesto sobre
     * el mismo sitio obliga a mantener pulsado, esperar, y a que el editor
     * distinga cuál de los tres se ha querido hacer. Un puesto por toque no tiene
     * ninguna de esas dudas, se deshace tocando el contrario, y con el bloque en
     * la mano recorrer cinco puestos son cinco toques que se ven llegar.
     *
     * El cursor se queda dentro del bloque movido: es lo que permite darle varias
     * veces seguidas sin volver a apuntar, y lo que mantiene la barra del teclado
     * en pantalla.
     */
    const moveBlock = useCallback((dir) => {
        const el = editorRef.current;
        const block = blockRef.current && el?.contains(blockRef.current) ? blockRef.current : currentBlock();
        if (!el || !block)
            return;
        const sibling = (dir < 0 ? block.previousElementSibling : block.nextElementSibling);
        if (!sibling)
            return;
        /* De dónde sale cada uno, para dejarlos volver. `offsetTop` y no el
           rectángulo contra la ventana: más abajo se desplaza la página para traer
           el bloque a la vista, y una medida contra la ventana se llevaría ese
           desplazamiento dentro. */
        const wasBlock = block.offsetTop;
        const wasSibling = sibling.offsetTop;
        if (dir < 0)
            el.insertBefore(block, sibling);
        else
            el.insertBefore(sibling, block);
        blockRef.current = block;
        /* Los dos se cruzan a la vista en vez de aparecer ya cambiados: sin esto,
           dos renglones parecidos se permutan de un fotograma para otro y no se ve
           cuál se ha movido —y menos dándole varias veces seguidas, que es como se
           usa—.
    
           Con la API de animaciones y no con `style.transform`: lo que hay aquí
           dentro se guarda tal cual —`commit()` serializa el `innerHTML`—, y un
           estilo puesto a mano acabaría escrito dentro del post si se guarda a mitad
           de la animación. Así el documento no se toca. */
        if (!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
            for (const [node, was] of [[block, wasBlock], [sibling, wasSibling]]) {
                const shift = was - node.offsetTop;
                if (!shift)
                    continue;
                node.animate([{ transform: `translateY(${shift}px)` }, { transform: 'none' }], { duration: 200, easing: 'cubic-bezier(0.32, 0.72, 0, 1)' });
            }
        }
        el.focus();
        caretAtEnd(block);
        rememberRange();
        syncGutter();
        /* Que no se vaya de la pantalla: bajando desde el último renglón visible, el
           bloque acaba debajo del teclado y parece que no ha pasado nada. */
        block.scrollIntoView({ block: 'nearest' });
        onDraft?.(true);
        commit();
    }, [commit, currentBlock, onDraft, rememberRange, syncGutter]);
    /** Arrastre con orden provisional en vivo; Escape y cancelar restauran el original. */
    const startBlockDrag = useCallback((event) => {
        if (event.button !== 0)
            return;
        const el = editorRef.current;
        const block = blockRef.current && el?.contains(blockRef.current) ? blockRef.current : currentBlock();
        if (!el || !block)
            return;
        event.preventDefault();
        dragCleanup.current?.();
        const handle = event.currentTarget;
        const original = Array.from(el.childNodes);
        const startY = event.clientY;
        let pointerY = startY;
        let dragging = false;
        let done = false;
        let fade = null;
        let scrollFrame = 0;
        const animations = new Map();
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        handle.setPointerCapture?.(event.pointerId);
        let scroller = el.parentElement;
        while (scroller && !(/(auto|scroll)/.test(getComputedStyle(scroller).overflowY) && scroller.scrollHeight > scroller.clientHeight))
            scroller = scroller.parentElement;
        function place() {
            const blocks = Array.from(el.children);
            const others = blocks.filter((candidate) => candidate !== block);
            const target = others.find((candidate) => {
                // offsetTop is the final layout position, unaffected by the FLIP animation.
                const box = el.getBoundingClientRect();
                return pointerY < box.top + candidate.offsetTop - el.offsetTop + candidate.offsetHeight / 2;
            }) ?? null;
            if (target !== block.nextElementSibling) {
                const before = new Map(blocks.map((node) => [node, node.offsetTop]));
                el.insertBefore(block, target);
                for (const node of blocks) {
                    const delta = (before.get(node) ?? node.offsetTop) - node.offsetTop;
                    if (!delta || reduceMotion)
                        continue;
                    animations.get(node)?.cancel();
                    const animation = node.animate?.([{ transform: `translateY(${delta}px)` }, { transform: 'none' }], { duration: 200, easing: 'cubic-bezier(0.2, 0.75, 0.25, 1)' });
                    if (animation)
                        animations.set(node, animation);
                }
            }
        }
        function autoScroll() {
            const box = scroller ? scroller.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
            const speed = pointerY < box.top + 64 ? -Math.min(18, (box.top + 64 - pointerY) / 3)
                : pointerY > box.bottom - 64 ? Math.min(18, (pointerY - box.bottom + 64) / 3) : 0;
            if (speed) {
                if (scroller)
                    scroller.scrollTop += speed;
                else
                    window.scrollBy(0, speed);
                place();
            }
            scrollFrame = requestAnimationFrame(autoScroll);
        }
        function onMove(moveEvent) {
            pointerY = moveEvent.clientY;
            if (!dragging) {
                if (Math.abs(pointerY - startY) < 5)
                    return;
                dragging = true;
                dragActive.current = true;
                setIsDragging(true);
                setMenu(null);
                fade = block.animate?.([{ opacity: 0.3 }], { duration: reduceMotion ? 0 : 120, fill: 'forwards' }) ?? null;
                scrollFrame = requestAnimationFrame(autoScroll);
            }
            moveEvent.preventDefault();
            place();
        }
        function finish(cancel, unmount = false) {
            if (done)
                return;
            done = true;
            handle.removeEventListener('pointermove', onMove);
            handle.removeEventListener('pointerup', onUp);
            handle.removeEventListener('pointercancel', onCancel);
            handle.removeEventListener('lostpointercapture', onCancel);
            document.removeEventListener('keydown', onKey, true);
            cancelAnimationFrame(scrollFrame);
            fade?.cancel();
            animations.forEach((animation) => animation.cancel());
            if (handle.hasPointerCapture?.(event.pointerId))
                handle.releasePointerCapture(event.pointerId);
            if (cancel && dragging)
                for (const node of original)
                    el.appendChild(node);
            dragActive.current = false;
            dragCleanup.current = null;
            if (unmount)
                return;
            setIsDragging(false);
            setHoveredBlock(null);
            if (!dragging)
                return;
            draggedRef.current = true;
            blockRef.current = block;
            el.focus({ preventScroll: true });
            caretAtEnd(block);
            rememberRange();
            syncGutter();
            const changed = original.some((node, index) => el.childNodes[index] !== node);
            if (!cancel && changed) {
                onDraft?.(true);
                commit();
            }
        }
        function onUp() { finish(false); }
        function onCancel() { finish(true); }
        function onKey(keyEvent) {
            if (keyEvent.key === 'Escape') {
                keyEvent.preventDefault();
                keyEvent.stopImmediatePropagation();
                finish(true);
            }
            else if (dragging) {
                keyEvent.preventDefault();
                keyEvent.stopImmediatePropagation();
            }
        }
        dragCleanup.current = () => finish(true, true);
        handle.addEventListener('pointermove', onMove);
        handle.addEventListener('pointerup', onUp);
        handle.addEventListener('pointercancel', onCancel);
        handle.addEventListener('lostpointercapture', onCancel);
        document.addEventListener('keydown', onKey, true);
    }, [commit, currentBlock, onDraft, rememberRange, syncGutter]);
    /** Una copia del bloque justo debajo, con el cursor al final de la copia. */
    const duplicateBlock = useCallback(() => {
        const el = editorRef.current;
        const block = blockRef.current && el?.contains(blockRef.current) ? blockRef.current : currentBlock();
        if (!el || !block)
            return;
        const copy = block.cloneNode(true);
        copy.removeAttribute('data-block-hint');
        el.insertBefore(copy, block.nextSibling);
        blockRef.current = copy;
        el.focus();
        caretAtEnd(copy);
        rememberRange();
        syncGutter();
        onDraft?.(true);
        commit();
    }, [commit, currentBlock, onDraft, rememberRange, syncGutter]);
    /** Quita el bloque entero y deja el cursor en el de arriba (o el de abajo). */
    const deleteBlock = useCallback(() => {
        const el = editorRef.current;
        const block = blockRef.current && el?.contains(blockRef.current) ? blockRef.current : currentBlock();
        if (!el || !block)
            return;
        const next = (block.previousElementSibling ?? block.nextElementSibling);
        block.remove();
        let target = next;
        if (!el.firstElementChild) {
            el.innerHTML = EMPTY_BODY;
            target = el.querySelector('p');
        }
        blockRef.current = target;
        el.focus();
        if (target)
            caretAtEnd(target);
        rememberRange();
        syncGutter();
        measure();
        onDraft?.(true);
        commit();
    }, [commit, currentBlock, measure, onDraft, rememberRange, syncGutter]);
    /** Aplica un tipo de bloque de texto al bloque donde está el cursor. */
    const applyBlock = useCallback((block) => {
        const el = editorRef.current;
        if (!el)
            return;
        el.focus();
        restoreRange();
        if (block.action.kind === 'format') {
            document.execCommand('formatBlock', false, `<${block.action.tag}>`);
        }
        else if (block.action.kind === 'list') {
            document.execCommand(block.action.command);
        }
        else if (block.action.kind === 'html') {
            const html = block.action.html;
            insertBlockHtml(typeof html === 'function' ? html(t) : html, block.action.caretInside ?? false);
            return;
        }
        else {
            rememberRange();
            setMedia(block.action.media);
            return;
        }
        onDraft?.(true);
        commit();
        syncGutter();
    }, [commit, insertBlockHtml, onDraft, rememberRange, restoreRange, syncGutter, t]);
    /**
     * Quita el «/…» que abrió la lista antes de aplicar nada.
     *
     * Si no, el bloque se convierte en un título que empieza por «/imagen». Se
     * borra por texto y no por selección porque entre escribir y elegir el cursor
     * ha estado en otra parte —en la propia lista— y la selección ya no vale.
     */
    const dropSlashQuery = useCallback(() => {
        const block = blockRef.current;
        if (!block)
            return;
        const text = block.textContent ?? '';
        const slash = text.lastIndexOf('/');
        if (slash < 0)
            return;
        block.textContent = text.slice(0, slash);
        if (!block.textContent)
            block.innerHTML = '<br>';
        caretAtEnd(block);
        rememberRange();
    }, [rememberRange]);
    /** Convierte el bloque actual en otro, por su id. Lo usa la barra del teclado. */
    const turnIntoId = useCallback((id) => {
        const block = BLOCKS.find((candidate) => candidate.id === id);
        if (!block)
            return;
        rememberRange();
        if (block.turnInto)
            turnInto(block.turnInto);
        else
            applyBlock(block);
    }, [applyBlock, rememberRange, turnInto]);
    const runBlock = useCallback((block) => {
        const fromSlash = menu?.fromSlash ?? false;
        const turning = menu?.mode === 'turn';
        // Se ha elegido algo: el párrafo creado para la lista ya es de ese bloque.
        addedRef.current = null;
        setMenu(null);
        if (fromSlash)
            dropSlashQuery();
        if (turning && block.turnInto) {
            turnInto(block.turnInto);
            return;
        }
        applyBlock(block);
    }, [applyBlock, dropSlashQuery, menu, turnInto]);
    // ── Barra flotante sobre la selección ──────────────────────────────────────
    const [selectionBar, setSelectionBar] = useState(null);
    /* Lo que ya lleva la selección, para marcarlo en la barra: sin esto no se
       sabía si pulsar «negrita» la ponía o la quitaba. */
    const [marks, setMarks] = useState({ bold: false, italic: false, underline: false, strike: false, code: false, link: false, align: 'left' });
    const [alignOpen, setAlignOpen] = useState(false);
    const syncSelectionBar = useCallback(() => {
        const el = editorRef.current;
        const selection = window.getSelection();
        if (!el || !selection || selection.rangeCount === 0 || selection.isCollapsed || !el.contains(selection.anchorNode) || selectedImage()) {
            setSelectionBar(null);
            setAlignOpen(false);
            return;
        }
        const rect = selection.getRangeAt(0).getBoundingClientRect();
        const host = el.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) {
            setSelectionBar(null);
            return;
        }
        /* Que no se salga por los lados: la barra mide ya lo suyo —convertir,
           cinco formatos y el enlace— y centrada sobre una palabra del margen
           asomaba media fuera del editor. */
        const half = 150;
        const center = rect.left - host.left + rect.width / 2;
        const left = host.width > half * 2 ? Math.min(Math.max(center, half), host.width - half) : host.width / 2;
        setSelectionBar({ top: rect.top - host.top - 44, left });
        const anchor = selection.anchorNode;
        const inside = (selector) => Boolean((anchor instanceof Element ? anchor : anchor?.parentElement)?.closest(selector));
        setMarks({
            bold: document.queryCommandState('bold'),
            italic: document.queryCommandState('italic'),
            underline: document.queryCommandState('underline'),
            strike: document.queryCommandState('strikeThrough'),
            code: inside('code') && !inside('pre'),
            link: inside('a'),
            align: document.queryCommandState('justifyCenter') ? 'center'
                : document.queryCommandState('justifyRight') ? 'right'
                    : document.queryCommandState('justifyFull') ? 'justify'
                        : 'left',
        });
    }, [selectedImage]);
    useEffect(() => {
        const onSelectionChange = () => {
            if (!focused.current)
                return;
            syncSelectionBar();
            syncGutter();
        };
        document.addEventListener('selectionchange', onSelectionChange);
        return () => document.removeEventListener('selectionchange', onSelectionChange);
    }, [syncGutter, syncSelectionBar]);
    function run(command, arg) {
        const el = editorRef.current;
        if (!el)
            return;
        el.focus();
        document.execCommand(command, false, arg);
        onDraft?.(true);
        commit();
        syncSelectionBar();
    }
    /** Código en línea, como el `…` de Notion: lo pone sobre la selección o lo quita si ya lo es. */
    function toggleInlineCode() {
        const el = editorRef.current;
        const selection = window.getSelection();
        if (!el || !selection || selection.rangeCount === 0)
            return;
        const anchor = selection.anchorNode;
        const code = (anchor instanceof Element ? anchor : anchor?.parentElement)?.closest('code');
        if (code && el.contains(code) && !code.closest('pre')) {
            code.replaceWith(document.createTextNode(code.textContent ?? ''));
        }
        else if (!selection.isCollapsed) {
            document.execCommand('insertHTML', false, `<code>${escapeText(selection.toString())}</code>`);
        }
        else {
            return;
        }
        onDraft?.(true);
        commit();
        syncSelectionBar();
    }
    function openLinkPopup() {
        rememberRange();
        linkBlockRef.current = currentBlock();
        setLinkOpen(true);
    }
    /** El enlace de la barra: lo pone, o lo quita si la selección ya está en uno. */
    function toggleLink() {
        const el = editorRef.current;
        const selection = window.getSelection();
        const anchor = selection?.anchorNode;
        const link = (anchor instanceof Element ? anchor : anchor?.parentElement)?.closest('a');
        if (el && selection && link && el.contains(link) && !link.closest('.cms-cta')) {
            const range = document.createRange();
            range.selectNodeContents(link);
            selection.removeAllRanges();
            selection.addRange(range);
            run('unlink');
            return;
        }
        openLinkPopup();
    }
    function applyLink(href) {
        const el = editorRef.current;
        if (!el)
            return;
        const selection = restoreRange();
        const selected = selection && selection.rangeCount > 0 ? selection.toString() : '';
        const anchor = selection?.anchorNode;
        const buttonLink = (anchor instanceof Element ? anchor : anchor?.parentElement)?.closest('.cms-cta a');
        if (buttonLink && el.contains(buttonLink))
            buttonLink.setAttribute('href', href);
        else if (selected)
            document.execCommand('createLink', false, href);
        else
            document.execCommand('insertHTML', false, `<a href="${escapeAttr(href)}">${escapeText(href)}</a>`);
        savedRange.current = null;
        onDraft?.(true);
        commit();
    }
    // ── La imagen elegida y su barra de tamaño ────────────────────────────────
    /**
     * Dónde está la imagen elegida, medida contra el marco del editor.
     *
     * Se remide con `useLayoutEffect` y a mano tras cada cambio de ancho: las
     * barras van por encima, no dentro, así que nadie las mueve por ellas.
     */
    const measureFigure = useCallback(() => {
        const host = rootRef.current;
        if (!figure || !host || !host.contains(figure)) {
            setFigureBox(null);
            return;
        }
        const rect = figure.getBoundingClientRect();
        const box = host.getBoundingClientRect();
        setFigureBox({ top: rect.top - box.top, left: rect.left - box.left, width: rect.width, height: rect.height });
    }, [figure]);
    useLayoutEffect(() => { measureFigure(); }, [measureFigure]);
    function setFigureSize(size) {
        if (!figure)
            return;
        figure.setAttribute('data-size', size);
        figure.style.maxWidth = '';
        onDraft?.(true);
        commit();
        requestAnimationFrame(measureFigure);
    }
    /**
     * Arrastrar el canto de la imagen para darle el ancho, como los widgets del
     * dashboard.
     *
     * Los cuatro tamaños de siempre siguen ahí para ir rápido; esto es para
     * cuando el ancho concreto importa —una foto de producto que tiene que
     * cuadrar con la de al lado—. Se guarda como porcentaje del ancho del texto,
     * no en píxeles: el mismo post se lee en un teléfono.
     */
    function startFigureResize(event, side) {
        const el = editorRef.current;
        if (!figure || !el)
            return;
        event.preventDefault();
        event.stopPropagation();
        const handle = event.currentTarget;
        handle.setPointerCapture(event.pointerId);
        const editorRect = el.getBoundingClientRect();
        const figureRect = figure.getBoundingClientRect();
        const centered = figure.getAttribute('data-align') !== 'left';
        const anchor = centered ? figureRect.left + figureRect.width / 2 : figureRect.left;
        const apply = (clientX) => {
            const distance = centered ? Math.abs(clientX - anchor) * 2 : (clientX - anchor) * side;
            const percent = Math.round(Math.max(15, Math.min(100, (distance / editorRect.width) * 100)));
            figure.setAttribute('data-size', 'custom');
            figure.style.maxWidth = `${percent}%`;
            setFigureWidth(percent);
            measureFigure();
        };
        const onMove = (move) => apply(move.clientX);
        const onUp = () => {
            handle.removeEventListener('pointermove', onMove);
            handle.removeEventListener('pointerup', onUp);
            handle.removeEventListener('pointercancel', onUp);
            setFigureWidth(null);
            onDraft?.(true);
            commit();
        };
        handle.addEventListener('pointermove', onMove);
        handle.addEventListener('pointerup', onUp);
        handle.addEventListener('pointercancel', onUp);
    }
    function setFigureAlign(align) {
        if (!figure)
            return;
        figure.setAttribute('data-align', align);
        onDraft?.(true);
        commit();
        requestAnimationFrame(measureFigure);
    }
    function removeFigure() {
        if (!figure)
            return;
        figure.remove();
        pickFigure(null);
        onDraft?.(true);
        commit();
    }
    function handleKeyDown(event) {
        if (menu && (event.key === 'Escape' || event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter')) {
            if (event.key === 'Escape') {
                event.preventDefault();
                setMenu(null);
            }
            return;
        }
        const image = selectedImage();
        if (image && !event.metaKey && !event.ctrlKey && !event.altKey) {
            if (event.key === 'Enter' || ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(event.key)) {
                event.preventDefault();
                writeOutsideImage(image, event.key === 'ArrowUp' || event.key === 'ArrowLeft');
                return;
            }
            if (event.key.length === 1 && event.key !== 'Dead')
                writeOutsideImage(image);
        }
        if (event.metaKey || event.ctrlKey) {
            const key = event.key.toLowerCase();
            /* Los de Notion: ⌘B/I/U, ⌘⇧S tachado, ⌘E código, ⌘K enlace, ⌘D
               duplicar, ⌘⇧↑/↓ mover el bloque y ⌘⌥0–3
               para párrafo y títulos. `code` y no `key` para los números: con ⌥ la
               tecla escribe otro carácter en Mac. */
            if (key === 'b') {
                event.preventDefault();
                run('bold');
            }
            else if (key === 'd') {
                event.preventDefault();
                syncGutter();
                duplicateBlock();
            }
            else if (event.shiftKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
                event.preventDefault();
                syncGutter();
                moveBlock(event.key === 'ArrowUp' ? -1 : 1);
            }
            else if (key === 'i') {
                event.preventDefault();
                run('italic');
            }
            else if (key === 'u') {
                event.preventDefault();
                run('underline');
            }
            else if (key === 's' && event.shiftKey) {
                event.preventDefault();
                run('strikeThrough');
            }
            else if (key === 'e') {
                event.preventDefault();
                toggleInlineCode();
            }
            else if (key === 'k') {
                event.preventDefault();
                toggleLink();
            }
            else if (event.altKey && /^Digit[0-3]$/.test(event.code)) {
                event.preventDefault();
                syncGutter();
                turnIntoId(['text', 'h1', 'h2', 'h3'][Number(event.code.slice(5))]);
            }
            return;
        }
        const block = currentBlock();
        const kind = blockKindOf(block);
        /* Tab sangra una lista y ⇧Tab la saca, en vez de mandar el foco fuera. */
        if (event.key === 'Tab') {
            const anchor = window.getSelection()?.anchorNode;
            const inList = (anchor instanceof Element ? anchor : anchor?.parentElement)?.closest('li');
            if (inList) {
                event.preventDefault();
                run(event.shiftKey ? 'outdent' : 'indent');
            }
            return;
        }
        if (!block || !kind)
            return;
        /* Retroceso al principio de un título, una cita o un destacado lo vuelve
           párrafo en vez de pegarlo al de arriba: es deshacer la forma, no el texto. */
        if (event.key === 'Backspace' && UNWRAPPABLE.has(kind) && caretOffsetIn(block) === 0) {
            event.preventDefault();
            blockRef.current = block;
            turnInto({ kind: 'tag', tag: 'p' });
            return;
        }
        /* Enter en un título o una cita: vacío, vuelve a párrafo; al final, sigue
           con un párrafo debajo. Sin esto el navegador seguía la cita renglón tras
           renglón y había que buscar cómo salir de ella. ⇧Enter sigue siendo un
           salto de línea dentro. */
        if (event.key === 'Enter' && !event.shiftKey && (kind === 'h1' || kind === 'h2' || kind === 'h3' || kind === 'quote')) {
            const text = block.textContent ?? '';
            if (!text.trim()) {
                event.preventDefault();
                blockRef.current = block;
                turnInto({ kind: 'tag', tag: 'p' });
                return;
            }
            if (caretOffsetIn(block) === text.length) {
                event.preventDefault();
                const after = document.createElement('p');
                after.innerHTML = '<br>';
                block.after(after);
                caretAtEnd(after);
                rememberRange();
                syncGutter();
                onDraft?.(true);
            }
        }
    }
    function handleInput(event) {
        setHoveredBlock(null);
        onDraft?.(true);
        syncGutter();
        measure();
        /* Los atajos de Markdown, sólo en un párrafo y sólo al teclear: pegar
           «# algo» no debería convertir nada. */
        const typed = event.nativeEvent;
        const current = currentBlock();
        if (typed.inputType === 'insertText' && current && blockKindOf(current) === 'text') {
            const offset = caretOffsetIn(current);
            const prefix = offset === null ? '' : (current.textContent ?? '').slice(0, offset);
            const target = offset !== null && offset <= 4 ? markdownShortcut(prefix) : null;
            if (target) {
                dropLeadingText(current, prefix.length);
                blockRef.current = current;
                if (target === 'divider') {
                    if (!current.textContent)
                        current.innerHTML = '<br>';
                    insertBlockHtml('<hr>');
                }
                else {
                    turnInto(target);
                }
                setMenu(null);
                return;
            }
        }
        /* «/» al escribir abre la lista, como en Notion. Sólo cuenta si es lo
           último que se ha escrito y va precedido de nada o de un espacio: dentro de
           una URL —«https://…»— no debe abrirse. */
        const block = currentBlock();
        const text = block?.textContent ?? '';
        const slash = text.lastIndexOf('/');
        const before = slash > 0 ? text[slash - 1] : '';
        if (block && slash >= 0 && (slash === 0 || before === ' ' || before === ' ')) {
            const query = text.slice(slash + 1);
            if (!query.includes(' ')) {
                blockRef.current = block;
                setMenu({ top: block.offsetTop + block.offsetHeight + 6, anchorTop: block.offsetTop, query, fromSlash: true, mode: 'insert' });
                return;
            }
        }
        if (menu?.fromSlash)
            setMenu(null);
    }
    function handlePaste(event) {
        const image = selectedImage();
        if (image)
            writeOutsideImage(image);
        const plain = event.clipboardData.getData('text/plain');
        // Un enlace de vídeo pegado a secas se convierte en el reproductor, que es
        // lo que se quería decir al pegarlo.
        const embed = plain ? embedHtml(plain) : null;
        event.preventDefault();
        if (embed) {
            insertBlockHtml(embed);
            return;
        }
        document.execCommand('insertHTML', false, pastedToHtml(plain || event.clipboardData.getData('text/html').replace(/<[^>]*>/g, ' ')));
        onDraft?.(true);
        commit();
    }
    const hold = (event) => event.preventDefault();
    /** Abre la lista de bloques —para añadir o para convertir— sobre el actual. */
    /** El párrafo vacío que se creó para añadir algo (`openInsertBelow`), mientras la lista siga abierta. */
    const addedRef = useRef(null);
    const openBlockMenu = useCallback((mode, withActions = false) => {
        addedRef.current = null;
        rememberRange();
        const block = blockRef.current;
        setMenu({
            top: (block?.offsetTop ?? 0) + (block?.offsetHeight ?? 24) + 6,
            anchorTop: block?.offsetTop ?? 0,
            query: '',
            fromSlash: false,
            mode,
            withActions,
        });
    }, [rememberRange]);
    /**
     * La lista de insertar desde un botón («+», «Agregar elemento»): lo que se
     * elija va DEBAJO del bloque actual, nunca lo convierte.
     *
     * Los bloques de texto —párrafo, títulos, listas, cita— se aplican al bloque
     * donde está el cursor (`formatBlock`), que es lo que se quiere con la «/» en
     * un renglón vacío. Con un bloque con contenido, en cambio, pulsar «Agregar
     * elemento» y elegir un título convertía el último párrafo en título. Así que
     * si el bloque no está vacío se crea un párrafo vacío debajo, con el cursor
     * dentro, y la lista trabaja sobre ése.
     */
    const openInsertBelow = useCallback(() => {
        const el = editorRef.current;
        const block = blockRef.current && el?.contains(blockRef.current) ? blockRef.current : currentBlock();
        let fresh = null;
        if (el && block && !isBlankBlock(block)) {
            fresh = document.createElement('p');
            fresh.innerHTML = '<br>';
            el.insertBefore(fresh, block.nextSibling);
            caretAtEnd(fresh);
            syncGutter();
        }
        openBlockMenu('insert');
        addedRef.current = fresh;
    }, [currentBlock, openBlockMenu, syncGutter]);
    /**
     * Cierra la lista sin elegir nada: el párrafo vacío que se creó para ella se
     * quita. Un instante después y no en el acto: si se cerró por un clic en otro
     * bloque, el navegador aún tiene que colocar el cursor donde se pulsó, y
     * quitarlo antes movería esa diana.
     */
    const closeMenu = useCallback(() => {
        const added = addedRef.current;
        addedRef.current = null;
        setMenu(null);
        if (!added)
            return;
        window.setTimeout(() => {
            if (!added.isConnected || !isBlankBlock(added))
                return;
            const selection = window.getSelection();
            const inside = Boolean(selection?.anchorNode && added.contains(selection.anchorNode));
            const before = added.previousElementSibling;
            added.remove();
            if (inside && before) {
                blockRef.current = before;
                caretAtEnd(before);
                rememberRange();
            }
            syncGutter();
            measure();
        }, 0);
    }, [measure, rememberRange, syncGutter]);
    /** El cursor al final del último bloque, que pasa a ser «el bloque actual». */
    const caretToEnd = useCallback(() => {
        const el = editorRef.current;
        if (!el)
            return null;
        const last = el.lastElementChild;
        el.focus();
        if (last) {
            caretAtEnd(last);
        }
        syncGutter();
        return last;
    }, [syncGutter]);
    /** «Agregar elemento»: la lista de bloques, y lo elegido va detrás del último. */
    const addAtEnd = useCallback(() => {
        if (!caretToEnd() && !editorRef.current)
            return;
        openInsertBelow();
    }, [caretToEnd, openInsertBelow]);
    const toolsBlock = hoveredBlock && editorRef.current?.contains(hoveredBlock)
        ? hoveredBlock : gutter !== null ? blockRef.current : idleAdd ? editorRef.current?.lastElementChild : null;
    const toolsBlank = toolsBlock ? isBlankBlock(toolsBlock) : blockBlank;
    const toolsLine = toolsBlock ? parseFloat(getComputedStyle(toolsBlock).lineHeight) || 24 : 24;
    const toolsTop = toolsBlock ? toolsBlock.offsetTop + Math.min(toolsBlock.offsetHeight || toolsLine, toolsLine) / 2 : idleTop;
    function activateToolsBlock() {
        if (!toolsBlock || !editorRef.current?.contains(toolsBlock))
            return;
        blockRef.current = toolsBlock;
        if (currentBlock() !== toolsBlock) {
            editorRef.current.focus({ preventScroll: true });
            caretAtEnd(toolsBlock);
        }
        pickFigure(toolsBlock.matches('figure.cms-figure') ? toolsBlock : null);
        rememberRange();
        syncGutter();
    }
    function hoverBlock(event) {
        if (dragActive.current || menu || event.buttons)
            return;
        const el = editorRef.current;
        let node = event.target;
        if (!el || !node || !el.contains(node))
            return;
        if (node === el) {
            // La línea mide un píxel: se puede apuntar también al aire junto al trazo.
            const divider = Array.from(el.children).find((child) => {
                if (child.tagName !== 'HR')
                    return false;
                const rect = child.getBoundingClientRect();
                return rect.width > 0 && Math.abs(event.clientY - (rect.top + rect.height / 2)) <= 10;
            });
            setHoveredBlock(divider ?? null);
            return;
        }
        while (node && node.parentElement !== el)
            node = node.parentElement;
        if (node && node !== hoveredBlock)
            setHoveredBlock(node);
    }
    /**
     * Pulsación larga sobre un bloque: lo que en escritorio hace el asa.
     *
     * Medio segundo, y se cancela en cuanto el dedo se mueve o se levanta: es el
     * mismo umbral que usa el sistema para su propio menú de selección, así que
     * los dos gestos no se pisan —el suyo pide texto seleccionado; éste, no—.
     */
    const pressTimer = useRef(0);
    const cancelPress = useCallback(() => {
        if (!pressTimer.current)
            return;
        window.clearTimeout(pressTimer.current);
        pressTimer.current = 0;
    }, []);
    const startPress = useCallback(() => {
        cancelPress();
        pressTimer.current = window.setTimeout(() => {
            pressTimer.current = 0;
            // El cursor ya ha aterrizado donde se ha tocado: el bloque es el suyo.
            syncGutter();
            openBlockMenu('turn', true);
        }, 520);
    }, [cancelPress, openBlockMenu, syncGutter]);
    return (_jsxs("div", { onMouseMove: hoverBlock, onMouseLeave: () => { if (!dragActive.current)
            setHoveredBlock(null); }, className: `cms-blocks${isDragging ? ' is-dragging' : ''}${empty ? ' is-empty' : ''}${idleAdd ? ' is-blog' : ''}`, ref: rootRef, children: [_jsx("style", { children: `${contentCss}${EDITOR_TYPOGRAPHY_CSS}` }), hoveredBlock && toolsBlock && toolsBlock.tagName !== 'HR' && !menu && !isDragging ? (_jsx("span", { className: "cms-blocks-highlight", "aria-hidden": "true", style: { top: toolsBlock.offsetTop - 4, height: Math.max(24, toolsBlock.offsetHeight) + 8 } })) : null, toolsTop !== null && toolsBlock && !menu ? (_jsxs("div", { className: "cms-blocks-tools", style: { top: toolsTop }, children: [_jsx("button", { type: "button", className: `cms-blocks-add${toolsBlank ? '' : ' is-edit'}${gutter === null && !hoveredBlock ? ' is-idle' : ''}`, title: t(toolsBlank ? 'blk.add' : 'blk.editBlock'), "aria-label": t(toolsBlank ? 'blk.add' : 'blk.editBlock'), onMouseDown: hold, onClick: () => { activateToolsBlock(); if (toolsBlank)
                            openInsertBelow();
                        else
                            openBlockMenu('turn', true); }, children: toolsBlank ? _jsx(IconPlus, { size: 16 }) : _jsx(IconPencil, { size: 15 }) }), _jsx("button", { type: "button", className: "cms-blocks-turn", title: t('blk.blockOptions'), "aria-label": t('blk.blockOptions'), onMouseDown: hold, onPointerDown: (event) => { activateToolsBlock(); startBlockDrag(event); }, onClick: () => {
                            if (draggedRef.current) {
                                draggedRef.current = false;
                                return;
                            }
                            activateToolsBlock();
                            openBlockMenu('turn', true);
                        }, children: _jsx(IconGripVertical, { size: 16 }) })] })) : null, empty ? _jsx("span", { className: "cms-blocks-placeholder", "aria-hidden": "true", children: t('blk.placeholderLong') }) : null, menu ? (_jsx(BlockMenu, { top: menu.top, anchorTop: menu.anchorTop, query: menu.query, mode: menu.mode, currentKind: currentKind, onPick: runBlock, onClose: closeMenu, actions: menu.withActions ? [
                    { id: 'duplicate', label: t('blk.duplicate'), icon: _jsx(IconCopy, { size: 15 }), shortcut: '⌘D', run: duplicateBlock },
                    { id: 'up', label: t('blk.moveUpShort'), icon: _jsx(IconArrowUp, { size: 15 }), shortcut: '⌘⇧↑', disabled: blockEnds.first, run: () => moveBlock(-1) },
                    { id: 'down', label: t('blk.moveDownShort'), icon: _jsx(IconArrowDown, { size: 15 }), shortcut: '⌘⇧↓', disabled: blockEnds.last, run: () => moveBlock(1) },
                    { id: 'delete', label: t('blk.delete'), icon: _jsx(IconTrash, { size: 15 }), danger: true, run: deleteBlock },
                ] : undefined })) : null, selectionBar && !menu && !linkOpen ? (_jsxs("div", { className: "cms-blocks-selbar", style: { top: selectionBar.top, left: selectionBar.left }, onMouseDown: hold, children: [_jsxs("button", { type: "button", className: "cms-blocks-selbar-kind", title: t('blk.turnIntoAnother'), onClick: () => { syncGutter(); openBlockMenu('turn'); }, children: [_jsx("span", { children: t(BLOCKS.find((candidate) => candidate.id === currentKind)?.labelKey ?? 'blk.text') }), _jsx(IconChevronDown, { size: 12 })] }), _jsx("span", { className: "cms-blocks-selbar-sep" }), _jsx("button", { type: "button", className: marks.bold ? 'is-on' : '', title: `${t('type.bold')} · ⌘B`, "aria-pressed": marks.bold, onClick: () => run('bold'), children: _jsx(IconBold, { size: 14 }) }), _jsx("button", { type: "button", className: marks.italic ? 'is-on' : '', title: `${t('type.italic')} · ⌘I`, "aria-pressed": marks.italic, onClick: () => run('italic'), children: _jsx(IconItalic, { size: 14 }) }), _jsx("button", { type: "button", className: marks.underline ? 'is-on' : '', title: `${t('type.underline')} · ⌘U`, "aria-pressed": marks.underline, onClick: () => run('underline'), children: _jsx(IconUnderline, { size: 14 }) }), _jsx("button", { type: "button", className: marks.strike ? 'is-on' : '', title: `${t('type.strikethrough')} · ⌘⇧S`, "aria-pressed": marks.strike, onClick: () => run('strikeThrough'), children: _jsx(IconStrikethrough, { size: 14 }) }), _jsx("button", { type: "button", className: marks.code ? 'is-on' : '', title: `${t('blk.inlineCode')} · ⌘E`, "aria-pressed": marks.code, onClick: toggleInlineCode, children: _jsx(IconCode, { size: 14 }) }), _jsx("span", { className: "cms-blocks-selbar-sep" }), _jsxs("span", { className: "cms-blocks-selbar-align", children: [_jsxs("button", { type: "button", className: marks.align !== 'left' ? 'is-on' : '', title: t('blk.align'), "aria-expanded": alignOpen, onClick: () => setAlignOpen((open) => !open), children: [ALIGN_ICON[marks.align], _jsx(IconChevronDown, { size: 11 })] }), alignOpen ? (_jsx("span", { className: "cms-blocks-selbar-align-menu", role: "menu", children: TEXT_ALIGNS.map((align) => (_jsx("button", { type: "button", role: "menuitemradio", "aria-checked": marks.align === align, className: marks.align === align ? 'is-on' : '', title: t(`blk.align.${align}`), onClick: () => { run(ALIGN_COMMAND[align]); setAlignOpen(false); }, children: ALIGN_ICON[align] }, align))) })) : null] }), _jsx("span", { className: "cms-blocks-selbar-sep" }), _jsx("button", { type: "button", className: marks.link ? 'is-on' : '', title: `${t(marks.link ? 'blk.removeLink' : 'toolbar.link')} · ⌘K`, "aria-pressed": marks.link, onClick: toggleLink, children: _jsx(IconLink, { size: 14 }) })] })) : null, figure && figureBox ? (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: "cms-blocks-grip is-start", style: { top: figureBox.top + figureBox.height / 2, left: figureBox.left }, "aria-label": t('blk.narrowImage'), title: t('panels.dragWidth'), onPointerDown: (event) => startFigureResize(event, -1) }), _jsx("button", { type: "button", className: "cms-blocks-grip is-end", style: { top: figureBox.top + figureBox.height / 2, left: figureBox.left + figureBox.width }, "aria-label": t('blk.widenImage'), title: t('panels.dragWidth'), onPointerDown: (event) => startFigureResize(event, 1) }), figureWidth !== null ? (_jsxs("span", { className: "cms-blocks-figwidth", style: { top: figureBox.top + figureBox.height / 2, left: figureBox.left + figureBox.width / 2 }, children: [figureWidth, "%"] })) : null] })) : null, figure && figureBox ? (_jsxs("div", { className: "cms-blocks-figbar", style: { top: figureBox.top + 8, left: figureBox.left + figureBox.width / 2 }, onMouseDown: hold, children: [IMAGE_SIZES.map((size) => (_jsx("button", { type: "button", className: figure.getAttribute('data-size') === size.id ? 'is-active' : '', title: t('blk.width', { size: t(size.labelKey) }), onClick: () => setFigureSize(size.id), children: t(size.labelKey) }, size.id))), _jsx("span", { className: "cms-blocks-figbar-sep" }), _jsx("button", { type: "button", title: t('blk.toTheLeft'), onClick: () => setFigureAlign('left'), children: _jsx(IconAlignLeft, { size: 14 }) }), _jsx("button", { type: "button", title: t('blk.centered'), onClick: () => setFigureAlign('center'), children: _jsx(IconAlignCenter, { size: 14 }) }), _jsx("span", { className: "cms-blocks-figbar-sep" }), _jsx("button", { type: "button", title: t('blk.remove'), className: "is-danger", onClick: removeFigure, children: _jsx(IconTrash, { size: 14 }) })] })) : null, media ? (_jsx(BlockMediaPopup, { kind: media, onInsert: (html) => { insertBlockHtml(html); setMedia(null); }, onClose: () => setMedia(null) })) : null, linkOpen ? _jsx(BlockLinkPopup, { anchor: savedRange.current, fallback: linkBlockRef.current, onApply: applyLink, onClose: () => { setLinkOpen(false); restoreRange(); syncSelectionBar(); } }) : null, writing ? createPortal(_jsxs("div", { className: "cms-blocks-bar", 
                /* Lo que mide el teclado, más el hueco de la pastilla del navegador
                   —`--cms-bar-lift`, en la hoja de estilos—. */
                style: { bottom: `calc(${keyboard}px + var(--cms-bar-lift, 0px) + env(safe-area-inset-bottom, 0px))` }, onMouseDown: hold, onTouchStart: (event) => event.preventDefault(), children: [_jsx("button", { type: "button", className: "cms-blocks-bar-main", "aria-label": t('blk.add'), onClick: openInsertBelow, children: _jsx(IconPlus, { size: 17 }) }), _jsxs("span", { className: "cms-blocks-bar-scroll", children: [_jsx("button", { type: "button", "aria-label": t('blk.moveUp'), disabled: blockEnds.first, onClick: () => moveBlock(-1), children: _jsx(IconArrowUp, { size: 16 }) }), _jsx("button", { type: "button", "aria-label": t('blk.moveDown'), disabled: blockEnds.last, onClick: () => moveBlock(1), children: _jsx(IconArrowDown, { size: 16 }) }), _jsx("span", { className: "cms-blocks-bar-sep" }), _jsx("button", { type: "button", "aria-label": t('type.bold'), onClick: () => run('bold'), children: _jsx(IconBold, { size: 16 }) }), _jsx("button", { type: "button", "aria-label": t('type.italic'), onClick: () => run('italic'), children: _jsx(IconItalic, { size: 16 }) }), _jsx("button", { type: "button", "aria-label": t('type.underline'), onClick: () => run('underline'), children: _jsx(IconUnderline, { size: 16 }) }), _jsx("button", { type: "button", "aria-label": t('toolbar.link'), onClick: openLinkPopup, children: _jsx(IconLink, { size: 16 }) }), _jsx("span", { className: "cms-blocks-bar-sep" }), _jsx("button", { type: "button", "aria-label": t('blk.title1'), className: currentKind === 'h1' ? 'is-active' : '', onClick: () => turnIntoId('h1'), children: _jsx(IconH1, { size: 17 }) }), _jsx("button", { type: "button", "aria-label": t('blk.title2'), className: currentKind === 'h2' ? 'is-active' : '', onClick: () => turnIntoId('h2'), children: _jsx(IconH2, { size: 17 }) }), _jsx("button", { type: "button", "aria-label": t('blk.list'), className: currentKind === 'bullet' ? 'is-active' : '', onClick: () => turnIntoId('bullet'), children: _jsx(IconList, { size: 17 }) }), _jsx("button", { type: "button", "aria-label": t('blk.quote'), className: currentKind === 'quote' ? 'is-active' : '', onClick: () => turnIntoId('quote'), children: _jsx(IconBlockquote, { size: 17 }) }), _jsx("button", { type: "button", "aria-label": t('blk.paragraph'), className: currentKind === 'text' ? 'is-active' : '', onClick: () => turnIntoId('text'), children: _jsx(IconLetterCase, { size: 17 }) }), _jsx("span", { className: "cms-blocks-bar-sep" }), _jsx("button", { type: "button", "aria-label": t('blk.more'), onClick: () => openBlockMenu('turn', true), children: _jsx(IconGripVertical, { size: 16 }) })] }), _jsx("button", { type: "button", className: "cms-blocks-bar-done", "aria-label": t('common.done'), onClick: () => { editorRef.current?.blur(); setWriting(false); }, children: _jsx(IconCheck, { size: 18, stroke: 2.2 }) })] }), document.body) : null, _jsx("div", { ref: editorRef, className: "cms-blocks-editor", role: "textbox", "aria-multiline": "true", "aria-label": t('cms.type.content'), contentEditable: true, suppressContentEditableWarning: true, "data-placeholder": t('blk.placeholderLong'), onFocus: () => {
                    focused.current = true;
                    /* Un renglón nuevo es un `<p>`, no un `<div>`: con `div` los atajos,
                       la conversión y la pista de «escribe algo» no lo reconocían. */
                    document.execCommand('defaultParagraphSeparator', false, 'p');
                    setWriting(true);
                    syncGutter();
                }, onTouchStart: startPress, onTouchMove: cancelPress, onTouchEnd: cancelPress, onTouchCancel: cancelPress, onInput: handleInput, onKeyDown: (event) => { setHoveredBlock(null); handleKeyDown(event); }, onPaste: handlePaste, onClick: (event) => {
                    const target = event.target;
                    const clicked = target.closest('figure.cms-figure');
                    pickFigure(clicked);
                    /* El asa y el lápiz siguen al bloque del cursor: si el clic dejó el
                       cursor en otro sitio —el párrafo de al lado—, se lleva a la imagen
                       para que sean los de la imagen. */
                    if (clicked) {
                        editorRef.current?.focus({ preventScroll: true });
                        const range = document.createRange();
                        range.selectNode(clicked);
                        const selection = window.getSelection();
                        selection?.removeAllRanges();
                        selection?.addRange(range);
                    }
                    syncGutter();
                }, onBlur: (event) => {
                    if (dragActive.current)
                        return;
                    // Pulsar en la lista de bloques o en una barra no es salir del editor.
                    if (rootRef.current?.contains(event.relatedTarget))
                        return;
                    /* La barra del teclado cuelga del `body`, así que no está «dentro» del
                       editor: sin esto, pulsar cualquiera de sus botones se leía como
                       salir y la barra desaparecía bajo el dedo. */
                    if (event.relatedTarget?.closest?.('.cms-blocks-bar'))
                        return;
                    focused.current = false;
                    setWriting(false);
                    onDraft?.(false);
                    setSelectionBar(null);
                    setGutter(null);
                    measure();
                    commit();
                } }), idleAdd ? (_jsxs(_Fragment, { children: [_jsxs("button", { type: "button", className: "cms-blocks-addrow", onMouseDown: hold, onClick: addAtEnd, children: [_jsx(IconPlus, { size: 13, stroke: 2, "aria-hidden": "true" }), t('blk.addElement')] }), _jsx("div", { className: "cms-blocks-tail", onMouseDown: hold, onClick: () => { caretToEnd(); }, "aria-hidden": "true" })] })) : null] }));
}
function BlockMenu({ top, anchorTop, query, mode, currentKind, onPick, onClose, actions }) {
    const { t, language } = useEnv();
    const needle = query.trim().toLocaleLowerCase('es');
    // Convirtiendo sólo se ofrecen las formas a las que se puede llevar el texto:
    // ofrecer «Imagen» ahí sería prometer algo que no puede pasar.
    /* Una imagen o un separador no se convierten en texto: sólo se mueven,
       duplican o eliminan. */
    const turnable = currentKind !== 'image' && currentKind !== 'divider';
    const catalog = mode === 'turn' ? (turnable ? BLOCKS.filter((block) => block.turnInto) : []) : BLOCKS;
    const matches = catalog.filter((block) => !needle
        || t(block.labelKey).toLocaleLowerCase(language).includes(needle)
        || t(block.keywordsKey).includes(needle));
    const [active, setActive] = useState(0);
    useEffect(() => { setActive(0); }, [query]);
    /* Las flechas y el Enter se atienden desde el documento: el foco sigue en el
       texto —hace falta para no perder el cursor— así que las teclas nunca
       llegarían a esta lista. */
    useEffect(() => {
        const onKey = (event) => {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActive((index) => (index + 1) % Math.max(1, matches.length));
            }
            else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActive((index) => (index - 1 + matches.length) % Math.max(1, matches.length));
            }
            else if (event.key === 'Enter') {
                const block = matches[active];
                if (!block)
                    return;
                event.preventDefault();
                onPick(block);
            }
            else if (event.key === 'Escape') {
                event.preventDefault();
                onClose();
            }
        };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [active, matches, onClose, onPick]);
    useEffect(() => {
        const onDown = (event) => {
            if (event.target.closest('.cms-blocks-menu'))
                return;
            onClose();
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [onClose]);
    /**
     * Abajo si cabe; si no, hacia arriba.
     *
     * Escribiendo al final de un post largo, el bloque está pegado al borde de
     * abajo y la lista se abría fuera de la pantalla: había que desplazarse para
     * ver lo que se acababa de pedir, y para eso el desplegable estorba más de lo
     * que ayuda. Se mide después de dibujar y antes de pintar —`useLayoutEffect`—
     * así que el salto no llega a verse.
     *
     * Y si no cabe entera por ningún lado, se recorta al hueco que haya y se
     * desplaza por dentro, que es preferible a salirse de la ventana.
     */
    const menuRef = useRef(null);
    const [place, setPlace] = useState(null);
    useLayoutEffect(() => {
        const el = menuRef.current;
        if (!el)
            return;
        const rect = el.getBoundingClientRect();
        // De dónde a dónde va la caja en la pantalla, sea cual sea el sitio en el
        // que esté dibujada ahora mismo.
        const drawnTop = place?.top ?? top;
        const anchorScreenTop = rect.top - drawnTop + anchorTop;
        const belowScreenTop = rect.top - drawnTop + top;
        const margin = 12;
        /* Lo que de verdad se ve, no la ventana entera: con el teclado abierto,
           `innerHeight` sigue contando la mitad de pantalla que hay debajo de las
           teclas, así que la lista se creía con sitio de sobra y se abría detrás del
           teclado. */
        const visible = window.visualViewport?.height ?? window.innerHeight;
        const spaceBelow = visible - belowScreenTop - margin;
        const spaceAbove = anchorScreenTop - margin;
        const wanted = el.scrollHeight;
        /* Las opciones de un bloque —acciones más «Convertir en»— son casi 480px: con
           el tope de 320 de la lista de insertar salía una barra de desplazamiento
           dentro de un menú que se abre y se cierra. Con sitio, se enseña entera. */
        const cap = mode === 'turn' ? 500 : 320;
        const next = (wanted <= spaceBelow || spaceBelow >= spaceAbove)
            ? { top, maxHeight: Math.max(160, Math.min(cap, spaceBelow)) }
            : (() => {
                const height = Math.max(160, Math.min(cap, Math.min(wanted, spaceAbove)));
                return { top: anchorTop - height - 6, maxHeight: height };
            })();
        setPlace((current) => (current && current.top === next.top && current.maxHeight === next.maxHeight ? current : next));
        // `place` se lee para traducir coordenadas, pero no dispara la medición: el
        // sitio se calcula siempre desde el ancla, y tenerlo de dependencia lo haría
        // medirse en bucle.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [top, anchorTop, matches.length, mode]);
    let group = '';
    return (_jsxs("div", { ref: menuRef, className: `cms-blocks-menu${mode === 'turn' ? ' is-compact' : ''}`, style: { top: place?.top ?? top, maxHeight: place?.maxHeight ?? 320 }, onMouseDown: (event) => event.preventDefault(), children: [actions?.length ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "cms-blocks-menu-group", children: t('blk.actions') }), actions.map((action) => (_jsxs("button", { type: "button", className: `cms-blocks-menu-action${action.danger ? ' is-danger' : ''}`, disabled: action.disabled, onClick: () => { onClose(); action.run(); }, children: [action.icon, _jsx("span", { children: action.label }), action.shortcut ? _jsx("kbd", { className: "cms-blocks-menu-kbd", children: action.shortcut }) : null] }, action.id))), _jsx("span", { className: "cms-blocks-menu-sep" })] })) : null, mode === 'turn' && turnable ? _jsx("span", { className: "cms-blocks-menu-group", children: t('blk.turnInto') }) : null, matches.length === 0 && !(mode === 'turn' && !turnable) ? _jsx("p", { className: "cms-blocks-menu-empty", children: t('blk.noneNamed') }) : null, matches.map((block, index) => {
                const head = block.group !== group ? block.group : null;
                group = block.group;
                return (_jsxs("div", { children: [head && mode === 'insert' ? _jsx("span", { className: "cms-blocks-menu-group", children: t(GROUP_LABEL_KEY[head]) }) : null, _jsxs("button", { type: "button", className: `cms-blocks-menu-item${index === active ? ' is-active' : ''}`, onMouseEnter: () => setActive(index), onClick: () => onPick(block), children: [_jsx("span", { className: "cms-blocks-menu-icon", children: block.icon }), _jsxs("span", { className: "cms-blocks-menu-text", children: [_jsx("strong", { children: t(block.labelKey) }), mode === 'insert' ? _jsx("small", { children: t(block.hintKey) }) : null] }), mode === 'turn' && block.id === currentKind
                                    ? _jsx(IconCheck, { className: "cms-blocks-menu-check", size: 14 })
                                    : block.shortcut ? _jsx("kbd", { className: "cms-blocks-menu-kbd", children: block.shortcut }) : null] })] }, block.id));
            })] }));
}
// ── Insertar una imagen o un vídeo ───────────────────────────────────────────
function BlockMediaPopup({ kind, onInsert, onClose }) {
    const { t, renderImagePicker } = useEnv();
    const [url, setUrl] = useState('');
    function insertImage(src) {
        if (!src)
            return;
        // Nace a ancho completo, que es lo que casi siempre se quiere, y con la
        // barra de tamaño a un clic si no.
        onInsert(`<figure class="cms-figure" data-size="full" data-align="center"><img src="${escapeAttr(src)}" alt="" /></figure>`);
    }
    function insertVideo(raw) {
        const html = embedHtml(raw);
        if (html)
            onInsert(html);
    }
    function submit() {
        const value = url.trim();
        if (!value)
            return;
        if (kind === 'image')
            insertImage(value);
        else
            insertVideo(value);
    }
    if (kind === 'image') {
        return (_jsx(_Fragment, { children: renderImagePicker({
                onSelect: (src) => insertImage(src),
                onClose,
                footer: (_jsxs("div", { className: "cms-rich-media-url", children: [_jsx("input", { value: url, placeholder: t('cms.pasteImageUrl'), "aria-label": t('cms.imageUrl'), onChange: (event) => setUrl(event.target.value), onKeyDown: (event) => { if (event.key === 'Enter') {
                                event.preventDefault();
                                submit();
                            } } }), _jsx("button", { type: "button", disabled: !url.trim(), onClick: submit, children: t('cms.insert') })] })),
            }) }));
    }
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "cms-pop-backdrop", role: "presentation", onMouseDown: onClose }), _jsxs("div", { className: "cms-rich-media-pop", role: "dialog", "aria-label": t('blk.insertVideo'), children: [_jsxs("div", { className: "cms-rich-media-url", children: [_jsx("input", { autoFocus: true, value: url, placeholder: t('blk.pasteVideo'), onChange: (event) => setUrl(event.target.value), onKeyDown: (event) => { if (event.key === 'Enter') {
                                    event.preventDefault();
                                    submit();
                                } } }), _jsx("button", { type: "button", disabled: !url.trim(), onClick: submit, children: t('cms.insert') })] }), _jsx("p", { className: "cms-rich-media-hint", children: t('blk.videoHint') })] })] }));
}
function BlockLinkPopup({ anchor, fallback, onApply, onClose }) {
    const { t } = useEnv();
    const node = anchor?.startContainer;
    const link = (node instanceof Element ? node : node?.parentElement)?.closest('a');
    const currentHref = link?.getAttribute('href') ?? '';
    const [url, setUrl] = useState(currentHref === '#' ? '' : currentHref);
    const popupRef = useRef(null);
    const [place, setPlace] = useState(null);
    const positioned = place !== null;
    useLayoutEffect(() => {
        if (positioned)
            popupRef.current?.querySelector('input')?.focus({ preventScroll: true });
    }, [positioned]);
    useLayoutEffect(() => {
        function position() {
            const popup = popupRef.current;
            const host = popup?.parentElement;
            if (!popup || !host)
                return;
            const root = host.getBoundingClientRect();
            const range = anchor?.getBoundingClientRect?.();
            const target = range && range.height > 0 ? range : fallback?.getBoundingClientRect() ?? root;
            const box = popup.getBoundingClientRect();
            const viewport = window.visualViewport;
            const visibleTop = viewport?.offsetTop ?? 0;
            const visibleBottom = visibleTop + (viewport?.height ?? window.innerHeight);
            const minLeft = Math.max(8, root.left + 8);
            const maxLeft = Math.min(window.innerWidth - box.width - 8, root.right - box.width - 8);
            const screenLeft = Math.max(minLeft, Math.min(maxLeft, target.left + target.width / 2 - box.width / 2));
            const above = target.top - box.height - 10;
            const screenTop = Math.max(visibleTop + 8, Math.min(above >= visibleTop + 8 ? above : target.bottom + 10, visibleBottom - box.height - 8));
            setPlace({ top: screenTop - root.top, left: screenLeft - root.left });
        }
        position();
        window.addEventListener('scroll', position, true);
        window.addEventListener('resize', position);
        window.visualViewport?.addEventListener('resize', position);
        window.visualViewport?.addEventListener('scroll', position);
        return () => {
            window.removeEventListener('scroll', position, true);
            window.removeEventListener('resize', position);
            window.visualViewport?.removeEventListener('resize', position);
            window.visualViewport?.removeEventListener('scroll', position);
        };
    }, [anchor, fallback]);
    function submit() {
        const value = url.trim();
        if (!value)
            return;
        onApply(/^https?:\/\//i.test(value) || value.startsWith('/') || value.startsWith('#') ? value : `https://${value}`);
        onClose();
    }
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "cms-pop-backdrop", role: "presentation", onMouseDown: onClose }), _jsx("div", { ref: popupRef, className: "cms-rich-link-pop", role: "dialog", "aria-label": t('blk.insertLink'), style: { top: place?.top ?? 0, left: place?.left ?? 0, visibility: place ? 'visible' : 'hidden' }, onKeyDown: (event) => { if (event.key === 'Escape') {
                    event.preventDefault();
                    event.stopPropagation();
                    onClose();
                } }, children: _jsxs("form", { className: "cms-rich-media-url", onSubmit: (event) => { event.preventDefault(); submit(); }, children: [_jsx("input", { value: url, inputMode: "url", spellCheck: false, placeholder: t('link.urlPlaceholder'), "aria-label": t('blk.insertLink'), onChange: (event) => setUrl(event.target.value) }), _jsx("button", { type: "submit", disabled: !url.trim(), children: t('blk.link') })] }) })] }));
}
//# sourceMappingURL=BlockEditor.js.map